// Import React/Redux.
import * as React from 'react';

// Import internal components.
import { DebugEx } from '../../../../Utils/DebugEx'
import { JsonSchemaValidator } from "../../../../Utils/JsonSchemaValidator";
import * as WidgetsThingsStructs from '../WidgetsThingsStructs';
import { TileType, TileLink, Tile as PrimaryTile } from '../../../Primitive/Tile';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from "../../WidgetStructs";
import { IWidgetDataValidateResp } from "../../IWidgetDataValidateResp";
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";

// Misc.
import _ from 'lodash';

// ----------------------------------------------------------------------------------------------------------------

export function TileDescriptionJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance("TileDescriptionJsonSchemeValidator", {
        title: "TilePortData",
        type: "object",
        properties: {
            subject: {
                type: ["string", "number"],
            },
            stats: {
                type: ["string", "number"],
            },
            color: {
                type: "string",
            },
            icon: {
                type: "string",
            },
            iconColor: {
                type: "string",
            },
            textColor: {
                type: "string",
            },
            content: {
                type: ["string", "number"],
            },
            percentage: {
                type: "number",
                minimum: 0,
                maximum: 100
            },
        },
    });
}
// ----------------------------------------------------------
export interface PartialTileData {
    iconColor?: string;
    iconSizeCustom?: number;
    iconText?: string;
    iconTextPosition?: string;
    iconTextFontSize?: number;
    iconPosition?: string;
    iconBackgroundColorAlpha?: number;
    //  textColor?: string;
    statsFontSize?: number;
    statsHorizontalAligment?: string;
    statsFontWeight?: any;
    statsFontColor?: string; //todo
    subjectFontSize?: number;
    subjectHorizontalAligment?: string;
    subjectFontColor?: string;
    subjectBackgroundColorAlpha?: number;
    subjectFontWeight?: any;
    contentHorizontalAligment?: string;
    contentFontColor?: string;//todo
}
// ----------------------------------------------------------
export interface TileData {
    type: TileType;
    color: string;
    icon: string;
    iconColor: string;
    iconSizeInfo: number;
    iconSizeStat: number;
    iconSizeCustom: number;
    subject: string | number;
    content?: string | number;
    subjectFontSize: number;
    stats: string | number;
    statsFontSize: number;
    textColor: string;
    links?: TileLink[];

    statsHorizontalAligment?: string;
    statsFontWeight?: any;
    statsFontColor?: string;

    subjectHorizontalAligment?: string;
    subjectFontColor?: string;
    subjectBackgroundColorAlpha?: number;
    subjectFontWeight?: any;

    iconText?: string;
    iconTextPosition?: string;
    iconTextFontSize?: number;
    iconPosition?: string;
    iconBackgroundColorAlpha?: number;

    contentHorizontalAligment?: string;
    contentFontColor?: string;
}

export interface TileDescription {
    color: string;
    icon: string;
    iconColor: string;
    subject: string | number;
    content?: string | number;
    stats: string | number;
    textColor: string;
    percentage?: number;
    iconText?: string;

    /*Used in case of Custom tile ONLY.*/
    customData?: {
        content?: string | number;
        stats?: string | number;
        subject?: string | number;
        iconText?: string;
        icon?: string;
    };
    customSettings?: PartialTileData;
}

export interface TileState extends TileDescription {
    /**
     * Keep keys for labels which can be localized and their
     * value can also be set from a port event. On port event
     * remove the referenced label from localization.
     */
    localizableLabels: string[];
}

export interface TileSettingsState { }

// ----------------------------------------------------------------------------------------------------------------

class Tile extends React.Component<WidgetsThingsStructs.PartialIWidgetThingsProps<TileData>, TileState> /*implements WidgetsThingsStructs.IPortEvents*/ {
    // ------------------------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<TileData>) {
        super(props);

        this.state = {
            customSettings: {
                iconColor: this.props.WidgetData.iconColor,

                subjectFontSize: this.props.WidgetData.subjectFontSize,

                statsFontSize: this.props.WidgetData.statsFontSize,
                statsFontWeight: this.props.WidgetData.statsFontWeight,
                statsFontColor: this.props.WidgetData.statsFontColor,
                statsHorizontalAligment: this.props.WidgetData.statsHorizontalAligment,

                subjectHorizontalAligment: this.props.WidgetData.subjectHorizontalAligment,
                subjectFontColor: this.props.WidgetData.subjectFontColor,
                subjectBackgroundColorAlpha: this.props.WidgetData.subjectBackgroundColorAlpha,
                subjectFontWeight: this.props.WidgetData.subjectFontWeight,

                iconTextPosition: this.props.WidgetData.iconTextPosition,
                iconPosition: this.props.WidgetData.iconPosition,
                iconBackgroundColorAlpha: this.props.WidgetData.iconBackgroundColorAlpha,
                iconTextFontSize: this.props.WidgetData.iconTextFontSize,

                contentHorizontalAligment: this.props.WidgetData.contentHorizontalAligment,
                contentFontColor: this.props.WidgetData.contentFontColor
            },
            customData: {
                stats: this.props.WidgetData.stats,
                subject: this.props.WidgetData.subject,
                content: this.props.WidgetData.content,
                iconText: this.props.WidgetData.iconText,
                icon: this.props.WidgetData.icon
            },

            color: this.props.WidgetData.color,
            icon: this.props.WidgetData.icon,
            iconColor: this.props.WidgetData.iconColor,
            subject: this.props.WidgetData.subject,
            content: this.props.WidgetData.content,
            stats: this.props.WidgetData.stats,
            textColor: this.props.WidgetData.textColor,
            localizableLabels: this._initLocalizableLabels(this.props)
        };
    }
    // ---------------------------------------------------------------------------------------------------
    private _initLocalizableLabels(props: WidgetsThingsStructs.PartialIWidgetThingsProps<TileData>): string[] {
        // Initially all labels can be localized.
        const keys = [];
        if (_.has(props.LocalizationMap, LocalizationLabelKey.subject))
            keys.push(LocalizationLabelKey.subject);

        if (_.has(props.LocalizationMap, LocalizationLabelKey.content))
            keys.push(LocalizationLabelKey.content);

        if (_.has(props.LocalizationMap, LocalizationLabelKey.stats))
            keys.push(LocalizationLabelKey.stats);

        return keys;
    }
    // ---------------------------------------------------------------------------------------------
    private _localizeLabels(localizationMap: LocalizationMap) {
        // Get labels available for localization.
        const labels = this
            .state
            .localizableLabels;

        // Can we localize anything?
        if (_.isArray(labels)) {
            this.setState((s: TileState) => {
                _.forEach(labels, (v: string) => {
                    // Try to localize the label.
                    const value = localizationMap[v];
                    if (!isStringEmpty(value))
                        s[v] = value;
                });
                return s;
            });
        }
    }
    // ------------------------------------------------------------------------------------------------------------
    private get _localizeLinks(): TileLink[] {
        return _.map(
            _.cloneDeep(this.props.WidgetData.links),
            (v: TileLink) => {
                // Map link's label to its translation.
                const value = this
                    .props
                    .LocalizationMap[v.key];

                // Found translation?
                if (!isStringEmpty(value))
                    v.label = value;
                return v;
            });
    }
    // ------------------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: IWidgetThingsProps<TileData>) {
        if (!_.isEqual(prevProps.WidgetData, this.props.WidgetData))
            this.setState({
                customData: {
                    subject: this.props.WidgetData.subject,
                    content: this.props.WidgetData.content,
                    stats: this.props.WidgetData.stats,
                    iconText: this.props.WidgetData.iconText,
                    icon: this.props.WidgetData.icon
                },
                customSettings: {
                    iconColor: this.props.WidgetData.iconColor,

                    subjectFontSize: this.props.WidgetData.subjectFontSize,

                    statsFontSize: this.props.WidgetData.statsFontSize,
                    statsFontWeight: this.props.WidgetData.statsFontWeight,
                    statsHorizontalAligment: this.props.WidgetData.statsHorizontalAligment,
                    statsFontColor: this.props.WidgetData.statsFontColor,

                    subjectHorizontalAligment: this.props.WidgetData.subjectHorizontalAligment,
                    subjectFontColor: this.props.WidgetData.subjectFontColor,
                    subjectBackgroundColorAlpha: this.props.WidgetData.subjectBackgroundColorAlpha,
                    subjectFontWeight: this.props.WidgetData.subjectFontWeight,


                    iconTextPosition: this.props.WidgetData.iconTextPosition,
                    iconPosition: this.props.WidgetData.iconPosition,
                    iconBackgroundColorAlpha: this.props.WidgetData.iconBackgroundColorAlpha,
                    iconTextFontSize: this.props.WidgetData.iconTextFontSize,

                    contentHorizontalAligment: this.props.WidgetData.contentHorizontalAligment,
                    contentFontColor: this.props.WidgetData.contentFontColor
                },

                color: this.props.WidgetData.color,
                icon: this.props.WidgetData.icon,
                iconColor: this.props.WidgetData.iconColor,
                subject: this.props.WidgetData.subject,
                content: this.props.WidgetData.content,
                stats: this.props.WidgetData.stats,
                textColor: this.props.WidgetData.textColor,
                localizableLabels: this._initLocalizableLabels(this.props)
            });

        //update data from ports
        if (prevProps.PortVersion != this.props.PortVersion ||
            !_.isEqual(prevProps.PortsStates, this.props.PortsStates)) {
            _.forEach(this.props.PortsStates, (port, key) => {
                this.SetPortState(key,
                    port.State,
                    port.LastStateStatus == StateStatus.Valid);
            });
        }

        // Localization map changed?
        if (!_.isEqual(this.props.LocalizationMap, this.props.LocalizationMap))
            this._localizeLabels(this.props.LocalizationMap);
    }
    // ------------------------------------------------------------------------------------------------------------
    SetPortState(portKey: string, portState: any, isValid: boolean) {
        if (!isValid)
            return;

        const unlocalizeAndFetchLabels = (keyList: LocalizationLabelKey[]): string[] => {
            // Get localizable labels.
            const labels = _.cloneDeep(this
                .state
                .localizableLabels);

            // Remove label from localization.
            return _.size(keyList) > 0 ? _.pullAll(labels, keyList) : labels;
        };

        // Find to which port the event refers and update state.
        try {
            switch (portKey) {
                case PortKeys.Subject:
                    this.setState({
                        subject: portState,
                        localizableLabels: unlocalizeAndFetchLabels([LocalizationLabelKey.subject])
                    });
                    break;

                case PortKeys.Content:
                    this.setState({
                        content: portState,
                        localizableLabels: unlocalizeAndFetchLabels([LocalizationLabelKey.content])
                    });
                    break;

                case PortKeys.Stats:
                    this.setState({
                        stats: portState,
                        localizableLabels: unlocalizeAndFetchLabels([LocalizationLabelKey.stats])
                    });
                    break;

                case PortKeys.Percentage:
                    const percentage: number = parseInt(portState, 10);
                    this.setState({ percentage: _.isNaN(percentage) ? 0 : percentage });
                    break;

                case PortKeys.Color:
                    if (!isStringEmpty(portState))
                        this.setState({ color: portState });
                    break;

                case PortKeys.Icon:
                    this.setState({ icon: portState });
                    break;

                case PortKeys.IconColor:
                    if (!isStringEmpty(portState))
                        this.setState({ iconColor: portState });
                    break;

                case PortKeys.TextColor:
                    if (!isStringEmpty(portState))
                        this.setState({ textColor: portState });
                    break;

                case PortKeys.CustomData:
                    if (!isStringEmpty(portState)) {
                        const { customData } = this.state;

                        Object.keys(portState).forEach(key => {
                            if (customData[key] != portState[key])
                                customData[key] = portState[key]
                        });
                        this.setState({ customData: customData });
                    }
                    break;
                case PortKeys.CustomSettings:
                    if (!isStringEmpty(portState)) {
                        const { customSettings } = this.state;

                        let color = this.state.color;
                        Object.keys(portState).forEach(key => {
                            if (customSettings[key] != portState[key])
                                customSettings[key] = portState[key]

                            //Minor hack for updating the state color when we use custom settings
                            if (key == 'color') {
                                color = portState[key]
                            }
                        });
                        this.setState({ color: color, customSettings: customSettings });
                    }
                    break;
                case PortKeys.FullDescription:
                    // try to parse json (use try-catch to handle malf formed data passed in)
                    let data: TileDescription = portState;

                    // find which field should update
                    let newState: TileState = _.cloneDeep(this.state);
                    let newLabels: LocalizationLabelKey[] = [];

                    if (isDefined(data.subject)) {
                        newState.subject = data.subject;
                        newLabels.push(LocalizationLabelKey.subject);
                    }

                    if (isDefined(data.content)) {
                        newState.content = data.content;
                        newLabels.push(LocalizationLabelKey.content);
                    }

                    if (isDefined(data.stats)) {
                        newState.stats = data.stats;
                        newLabels.push(LocalizationLabelKey.stats);
                    }

                    if (isDefined(data.percentage))
                        newState.percentage = _.isNaN(data.percentage) ? 0 : data.percentage;

                    if (isDefined(data.color))
                        newState.color = data.color;

                    if (isDefined(data.icon))
                        newState.icon = data.icon;

                    if (isDefined(data.iconColor))
                        newState.iconColor = data.iconColor;

                    if (isDefined(data.textColor))
                        newState.textColor = data.textColor;

                    this.setState({
                        subject: newState.subject,
                        content: newState.content,
                        stats: newState.stats,
                        percentage: newState.percentage,
                        color: newState.color,
                        icon: newState.icon,
                        iconColor: newState.iconColor,
                        textColor: newState.textColor,
                        localizableLabels: unlocalizeAndFetchLabels(newLabels)
                    });
                    break;

                default:
                    break;
            }
        }
        catch (ex) {
            DebugEx.TraceError(ex);
        }
    }
    // ------------------------------------------------------
    componentDidMount() {
        // Check for initial localization.
        this._localizeLabels(this.props.LocalizationMap);

        // Update port
        if (isDefined(this.props.PortsStates)) {
            _.forEach(this.props.PortsStates, (port, key) => {
                this.SetPortState(key,
                    port.State,
                    port.LastStateStatus == StateStatus.Valid);
            });
        }
    }
    // ------------------------------------------------------------------------------------------------------------
    render() {
        return (
            <div
                style={{
                    width: this.props.width,
                    height: this.props.height,
                    padding: defaultWidgetContainerPadding,
                    backgroundColor: this.state.color
                }}
            >
                <PrimaryTile
                    width={this.props.width}
                    height={this.props.height}
                    type={this.props.WidgetData.type}
                    color={this.state.color}
                    icon={this.state.icon}
                    iconColor={this.state.iconColor}
                    iconSize={this.props.WidgetData.type == TileType.info ? this.props.WidgetData.iconSizeInfo : this.props.WidgetData.type == TileType.stat ? this.props.WidgetData.iconSizeStat : this.props.WidgetData.iconSizeCustom}
                    subject={this.state.subject}
                    subjectFontSize={this.props.WidgetData.subjectFontSize}
                    content={this.state.content}
                    stats={this.state.stats}
                    textColor={this.state.textColor}
                    progressBar={{ percent: this.state.percentage }}
                    links={this._localizeLinks}
                    onClickLink={(index: number) => this.props.SendPortEvent(PortKeys.Link + index, true)}
                    statsFontSize={this.props.WidgetData.statsFontSize}

                    customData={this.state.customData}
                    customSettings={this.state.customSettings}
                />
            </div>
        );
    }
    // ------------------------------------------------------------------------------------------------------------
}

// ----------------------------------------------------------------------------------------------------------------

export const colorPickerStyles = {
    container: {
        paddingTop: 6
    } as React.CSSProperties,
    root: {
        padding: 5,
        background: '#fff',
        borderRadius: '1px',
        boxShadow: '0 0 0 1px rgba(0, 0, 0, .1)',
        display: 'inline-block',
        cursor: 'pointer'
    } as React.CSSProperties,
    child: {
        width: 36,
        height: 14,
        borderRadius: 2
    } as React.CSSProperties,
    picker: {
        marginLeft: '-40%',
        position: 'absolute',
        zIndex: 2
    } as React.CSSProperties
};

// ----------------------------------------------------------------------------------------------------------------

class TileSettings extends React.Component<IWidgetSettingsProps<TileData>, TileSettingsState> {
    // ------------------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<TileData>) {
        super(props);

        // Initialize component's state.
        this.state = {};
    }
    // ------------------------------------------------------------------------------------------------------------
    render() {

        let settings: SettingsGroup<TileData>[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.DropDown,
                        label: 'Type',
                        tooltip: "Specify the type. Available options: Info and Stat.",
                        dataPath: "type",
                        items: [
                            { label: "Info", value: TileType.info },
                            { label: "Stat", value: TileType.stat },
                            { label: "Custom", value: TileType.custom }

                        ]
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Background Color',
                        tooltip: "Sets the widget's color.",
                        dataPath: "color",
                    },

                    /**Icon Settings*/
                    {
                        type: SettingsType.Icon,
                        label: "Icon",
                        tooltip: "Sets the widget's icon.",
                        dataPath: "icon",
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Icon Color',
                        tooltip: "Sets the widget's icon color.",
                        dataPath: "iconColor",
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Icon Size',
                        tooltip: "Sets the widget's icon size.",
                        dataPath: "iconSizeInfo",
                        maxValue: 100,
                        minValue: 10,
                        stepValue: 1,
                        valuePosfix: "px",
                        isHidden: (d) => d.type != TileType.info
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Icon Size',
                        tooltip: "Sets the widget's icon size.",
                        dataPath: "iconSizeStat",
                        maxValue: 100,
                        minValue: 10,
                        stepValue: 1,
                        valuePosfix: "px",
                        isHidden: (d) => d.type != TileType.stat
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Icon Size',
                        tooltip: "Sets the widget's icon size.",
                        dataPath: "iconSizeCustom",
                        maxValue: 100,
                        minValue: 10,
                        stepValue: 1,
                        valuePosfix: "px",
                        isHidden: (d) => d.type != TileType.custom
                    },
                    {
                        type: SettingsType.DropDown,
                        label: "Icon Position",
                        dataPath: "iconPosition", //todo change into iconPosition
                        tooltip: 'Set the icon position in the layout (left, bottom, right)',
                        items: [
                            { value: "left", label: 'Left' },
                            { value: 'bottom', label: 'Bottom' },
                            { value: 'right', label: 'Right' },
                            { value: 'top', label: 'Top' },

                        ],
                        isHidden: (d) => d.type != TileType.custom
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Icon Background Alpha',
                        tooltip: 'Sets Icon background alpha value',
                        maxValue: 1,
                        minValue: 0,
                        stepValue: 0.05,
                        valuePosfix: "",
                        dataPath: "iconBackgroundColorAlpha",
                        isHidden: (d) => d.type != TileType.custom
                    },
                    {
                        type: SettingsType.String,
                        label: 'Icon Text',
                        tooltip: 'Sets the Icon Text.',
                        dataPath: "iconText",
                        isHidden: (d) => d.type != TileType.custom

                    },
                    {
                        type: SettingsType.DropDown,
                        label: "Icon Text Position", //todo icon Text Position
                        dataPath: "iconTextPosition",
                        tooltip: 'Set the vertical  position of the icon (top, center, bottom)',
                        items: [
                            { value: "left", label: 'Left' },
                            { value: 'right', label: 'Right' },
                            { value: 'top', label: 'Top' },
                            { value: 'bottom', label: 'Bottom' }
                        ],
                        isHidden: (d) => d.type != TileType.custom
                    },

                    {
                        type: SettingsType.Slider,
                        label: 'Icon Text Font Size',
                        tooltip: 'Sets icon text size',
                        maxValue: 70,
                        minValue: 12,
                        stepValue: 1,
                        valuePosfix: "px",
                        dataPath: "iconTextFontSize",
                        isHidden: (d) => d.type != TileType.custom
                    },

                    /*Subject Settings*/
                    {
                        type: SettingsType.String,
                        label: 'Subject',
                        tooltip: 'Sets the title.',
                        dataPath: "subject",
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Subject Font Size',
                        tooltip: 'Sets font size',
                        maxValue: 70,
                        minValue: 12,
                        stepValue: 1,
                        valuePosfix: "px",
                        dataPath: "subjectFontSize",
                        isHidden: (d) => d.type == TileType.info //todo test backwards compatibility
                    },
                    {
                        type: SettingsType.DropDown,
                        label: "Subject Font Weight",
                        dataPath: "subjectFontWeight",
                        tooltip: 'Choose Subject font weight',
                        items: [
                            { value: "bold", label: 'Bold' },
                            { value: "normal", label: 'Normal' },
                            { value: "bolder", label: 'Bolder' },
                            { value: "lighter", label: 'Lighter' },
                            { value: "100", label: '100' },
                            { value: "200", label: '200' },
                            { value: "300", label: '300' },
                            { value: "400", label: '400' },
                            { value: "500", label: '500' },
                            { value: "600", label: '600' },
                            { value: "700", label: '700' },
                            { value: "800", label: '800' },
                            { value: "900", label: '900' },
                        ],
                        isHidden: (d) => d.type != TileType.custom
                    },
                    {
                        type: SettingsType.DropDown,
                        label: "Subject Align Horizontal",
                        dataPath: "subjectHorizontalAligment",
                        tooltip: 'Set the horizontal position of the Subject  (left, center, right)',
                        items: [
                            { value: "flex-start", label: 'Left' },
                            { value: 'center', label: 'Center' },
                            { value: 'flex-end', label: 'Right' },
                        ],
                        isHidden: (d) => d.type != TileType.custom
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Subject Font Color',
                        tooltip: "Sets the widget's subject text color.",
                        dataPath: "subjectFontColor",
                        isHidden: (d) => d.type != TileType.custom
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Subject Background Alpha',
                        tooltip: 'Sets subject background alpha value',
                        maxValue: 1,
                        minValue: 0,
                        stepValue: 0.05,
                        valuePosfix: "",
                        dataPath: "subjectBackgroundColorAlpha",
                        isHidden: (d) => d.type != TileType.custom
                    },
                    {
                        type: SettingsType.String,
                        label: "Content",
                        tooltip: "Used only when type is Info. Sets the content.",
                        dataPath: "content",
                        isHidden: (d) => d.type == TileType.stat
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Content Font Color',
                        tooltip: "Sets the widget's content font color.",
                        dataPath: "contentFontColor",
                        isHidden: (d) => d.type != TileType.custom
                    },
                    {
                        type: SettingsType.DropDown,
                        label: "Content Align Horizontal",
                        dataPath: "contentHorizontalAligment",
                        tooltip: 'Set the horizontal position of the content  (left, center, right)',
                        items: [
                            { value: "flex-start", label: 'Left' },
                            { value: 'center', label: 'Center' },
                            { value: 'flex-end', label: 'Right' },
                        ],
                        isHidden: (d) => d.type != TileType.custom
                    },

                    /*Stats Settings*/
                    {
                        type: SettingsType.String,
                        label: 'Stats',
                        tooltip: "Sets the Stats.",
                        dataPath: "stats",
                    },
                    {
                        type: SettingsType.DropDown,
                        label: "Stats Font Weight",
                        dataPath: "statsFontWeight",
                        tooltip: 'Choose stats font weight',
                        items: [
                            { value: "bold", label: 'Bold' },
                            { value: "normal", label: 'Normal' },
                            { value: "bolder", label: 'Bolder' },
                            { value: "lighter", label: 'Lighter' },
                            { value: "100", label: '100' },
                            { value: "200", label: '200' },
                            { value: "300", label: '300' },
                            { value: "400", label: '400' },
                            { value: "500", label: '500' },
                            { value: "600", label: '600' },
                            { value: "700", label: '700' },
                            { value: "800", label: '800' },
                            { value: "900", label: '900' },

                        ],
                        isHidden: (d) => d.type != TileType.custom

                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Stats Font Color',
                        tooltip: "Sets the widget's stats font color.",
                        dataPath: "statsFontColor",
                        isHidden: (d) => d.type != TileType.custom
                    },

                    {
                        type: SettingsType.DropDown,
                        label: "Stats Align Horizontal",
                        dataPath: "statsHorizontalAligment",
                        tooltip: 'Set the horizontal position of the stats (left, center, right)',
                        items: [
                            { value: "flex-start", label: 'Left' },
                            { value: 'center', label: 'Center' },
                            { value: 'flex-end', label: 'Right' },
                        ],
                        isHidden: (d) => d.type != TileType.custom

                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Stats Font Size',
                        tooltip: 'Set stats size',
                        maxValue: 70,
                        minValue: 12,
                        stepValue: 1,
                        valuePosfix: "px",
                        dataPath: "statsFontSize",
                        isHidden: (d) => d.type != TileType.stat
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Text Color',
                        tooltip: "Sets the color of the text.",
                        dataPath: "textColor",
                        isHidden: (d) => d.type == TileType.custom
                    },
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: 'Links',
                        tooltip: "Used only when type is Stat. Sets multiple links that can be only port events to cyan for internal signal or you can define a url link that is open external page.",
                        dataPath: "links",
                        createNewItem: (d) => {
                            return {
                                label: '',
                                url: '',
                                key: LocalizationLabelKey.link + getRandomKey()
                            }
                        },
                        itemsInfo: [
                            {
                                type: SettingsType.String,
                                label: 'Label',
                                dataPath: "label"
                            },
                            {
                                type: SettingsType.String,
                                label: 'URL',
                                dataPath: "url"
                            }
                        ],
                        isHidden: (d) => d.type == TileType.info || d.type == TileType.custom
                    },
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
}

// ----------------------------------------------------------------------------------------------------------------
import { LocalizationMap } from '@Primitive/Localization/Localization';
import { getRandomKey, isDefined, IsNullOrEmpty, isStringEmpty } from '@Utils/Micro';
import { defaultWidgetContainerPadding, IWidgetThingsProps } from '../WidgetsThingsStructs';
import { WebNode } from '@API/WebNode';
import { StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { WebThing, WebThingPort } from '@API/WebThing';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { SettingsList } from '@Primitive/SettingsList';
import { Plegma } from '@API/Api';
// ----------------------------------------------------------------------------------------------------------------
/**
 * The keys are the same as in TileData interface.
 * For links the suffix needs to be set explicitly.
 */
const enum PortKeys {
    Subject = "subject",
    Stats = "stats",
    Color = "color",
    Icon = "icon",
    IconColor = "icon_color",
    TextColor = "text_color",
    Content = "content",
    Percentage = "percentage",
    Link = "link_",
    FullDescription = "full_description",
    CustomSettings = 'CustomSettings',
    CustomData = 'CustomData'

}
const enum LocalizationLabelKey {
    subject = 'subject',
    content = 'content',
    stats = 'stats',
    link = 'link_'
}
// ----------------------------------------------------------------------------------------------------------------
export class TileWidget extends IWidgetThingsClass<TileData> {
    // ------------------------------------------------------------------------------------------------------------
    Render(key: string, width: number, height: number): React.ReactNode {
        return <Tile />;
    }
    // ------------------------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <TileSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------------------
    private createThingPorts(widgetData: TileData): WebThingPort[] {
        let ports: WebThingPort[] = [];

        //General Ports for stat and info
        if (widgetData.type !== TileType.custom) {
            ports.push(new WebThingPort({
                Name: 'Subject',
                PortKey: PortKeys.Subject,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'The tile\'s subject',
                State: ''
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'Stats',
                PortKey: PortKeys.Stats,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'The tile\'s stats',
                State: ''
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'Color',
                PortKey: PortKeys.Color,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'The tile\'s color',
                State: ''
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'Icon',
                PortKey: PortKeys.Icon,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'The tile\'s icon',
                State: ''
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'Icon Color',
                PortKey: PortKeys.IconColor,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'The tile\'s icon color',
                State: ''
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'Text Color',
                PortKey: PortKeys.TextColor,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'The tile\'s text color',
                State: ''
            }, WebThingPortType.Data));

            ports.push(new WebThingPort(
                {
                    Name: 'Full Description',
                    PortKey: PortKeys.FullDescription,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: 'The tile\'s full description',
                    State: ""
                }, WebThingPortType.Data, (data) => this.ValidateValues(PortKeys.FullDescription, data)
            ));
        }

        /*
        In case of custom we get setting and data through JSON's
        */
        if (widgetData.type == TileType.custom) {

            ports.push(new WebThingPort(
                {
                    Name: "Settings",
                    PortKey: PortKeys.CustomSettings,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "Tile's settings.",
                    State: ""
                },
                WebThingPortType.Data
                // (data) => this.ValidateValues(PortKeys.Data, data)
            ));
            ports.push(new WebThingPort(
                {
                    Name: "Data",
                    PortKey: PortKeys.CustomData,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "Tile's data.",
                    State: ""
                },
                WebThingPortType.Data
                // (data) => this.ValidateValues(PortKeys.Data, data)
            ));
        }

        if (widgetData.type == TileType.info) {
            ports.push(new WebThingPort({
                Name: 'Content',
                PortKey: PortKeys.Content,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'The tile\'s content',
                State: ''
            }, WebThingPortType.Data));

            ports.push(new WebThingPort({
                Name: 'Percentage',
                PortKey: PortKeys.Percentage,
                Type: Plegma.ePortType.Decimal,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'The representation of stats by percentage',
                State: ''
            }, WebThingPortType.Data));
        }

        if (widgetData.type == TileType.stat)
            _.forEach(widgetData.links, (l: TileLink, i: number) => {
                ports.push(new WebThingPort({
                    Name: l.label,
                    PortKey: PortKeys.Link + i,
                    Type: Plegma.ePortType.Boolean,
                    ioDirection: Plegma.ioPortDirection.Output,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: 'Represents the click of widget\'s link',
                    State: ''
                }, WebThingPortType.Trigger));
            });

        return ports;
    }
    // ------------------------------------------------------------------------------------------------------------
    updateThing() {
        let widgetData = this.WidgetData,
            webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        webThing.Ports = {};

        // Add thing's ports.
        this
            .createThingPorts(widgetData)
            .forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode
            .GetModule(this.WebModuleKey)
            .UpdateThing(webThing);
    }
    // ------------------------------------------------------------------------------------------------------------
    initThing() {
        let widgetData = (this.WidgetData as TileData),

            // Create plegma thing.
            plegmaThing = WidgetDescriptionToPlegmaThing(this.Description),

            // Create thing's ports and web thing.
            ports = this.createThingPorts(widgetData),
            webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): TileData {
        return _.cloneDeep({
            type: TileType.info,
            color: '#0073b7',
            icon: 'fa fa-info',
            iconColor: 'white',
            iconSizeInfo: 30,
            iconSizeStat: 45,
            iconSizeCustom: 12,
            iconTextPosition: "left",
            iconPosition: "left",
            subject: 'Subject',
            content: 'Content',
            stats: 'Stats',
            textColor: 'white',
            links: [],
            subjectFontSize: 20,
            statsFontSize: 30,
            iconText: "Icon Text",


            statsFontWeight: 'bold',
            statsFontColor: 'white',
            subjectFontWeight: 'bold',
            subjectFontColor: 'white',
            contentHorizontalAligment: 'flex-start',
            contentFontColor: 'white',
            subjectBackgroundColorAlpha: 0,
            iconBackgroundColorAlpha: 0.1,
            iconTextFontSize: 14

        });
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return TileWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        return {
            Title: 'Tile',
            WidgetTitle: '',
            Key: 'TileWidget',
            Type: 'TileWidget',
            Data: TileWidget.GetDefaultWidgetDataStatic(),
            Description: 'Displays basic info, highlights numbers or stats.',
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/tile.svg'
        };
    }
    // ------------------------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: TileData): TileData {
        // Migrate theme
        if (isDefined((data as any).theme)) {
            switch ((data as any).theme) {
                case 'bg-blue':
                    data.color = "#0073b7"
                    break;
                case 'bg-green':
                    data.color = "#00a65a"
                    break;
                case 'bg-yellow':
                    data.color = "#f39c12"
                    break;
                case 'bg-red':
                    data.color = "#dd4b39"
                    break;
            }
        }
        // Add any missing key to existing links.
        _.forEach(data.links, (v: TileLink) => {
            if (!isDefined(v.key))
                v.key = LocalizationLabelKey.link + getRandomKey();
        });
        return data;
    }
    // ------------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == PortKeys.FullDescription)
            return TileDescriptionJsonSchemeValidator().DataValidateString(value);
        return { valid: true, errors: '' };
    };
    // ------------------------------------------------------------------------------------------------
    GetLocalizationDictionary(): LocalizationMap {
        // start with empty dictionary
        const dict = {};

        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            dict[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        // add `subject` if defined
        if (!IsNullOrEmpty(this.WidgetData.subject as string))
            dict[LocalizationLabelKey.subject] = this.WidgetData.subject;

        // add `content` if defined
        if (!IsNullOrEmpty(this.WidgetData.content as string))
            dict[LocalizationLabelKey.content] = this.WidgetData.content;

        // add `stats` if defined
        if (!IsNullOrEmpty(this.WidgetData.stats as string))
            dict[LocalizationLabelKey.stats] = this.WidgetData.stats;

        // Set the dynamic labels for links.
        _.forEach(this.WidgetData.links, (v: TileLink) => {
            // add key for current `label` if defined
            if (!IsNullOrEmpty(v.label))
                dict[v.key] = v.label;
        });

        return dict;
    };
    // ------------------------------------------------------------------------------------------------------------
    GetDataPortKeyStatic(): string { return PortKeys.FullDescription; };
    // ---------------------------------------------------------------------------------------------
}